import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { normalizeLang } from "@/lib/i18n";
import { UPLOAD_STRINGS } from "@/lib/uploadI18n";
import { useAuth } from "@/context/AuthContext";
import { UserMenu } from "@/components/UserMenu";
import AppHeader from "@/components/AppHeader";

// Upload.jsx still consumes legacy credit-named translation keys. Override only the
// user-facing copy here so no credit terminology leaks into this flow. Paid accounts
// have up to 10 sessions in their 30-day access (decided 2026-10-30): what is sold is
// access with a usage limit, so the copy never says "credits" or "today".
export const SESSION_LIMIT_COPY = {
  en: {
    errInsufficientCredits: "You’ve used all the interview sessions in your 30-day access. Your reports are still available.",
    noCreditsTitle: "Interview session limit reached",
    noCreditsBody: "You’ve used all the interview sessions in your 30-day access. Your reports are still available.",
    creditsBalance: "Interview sessions",
  },
  "pt-BR": {
    errInsufficientCredits: "Você já usou todas as sessões de entrevista do seu acesso de 30 dias. Seus relatórios continuam disponíveis.",
    noCreditsTitle: "Limite de sessões de entrevista atingido",
    noCreditsBody: "Você já usou todas as sessões de entrevista do seu acesso de 30 dias. Seus relatórios continuam disponíveis.",
    creditsBalance: "Sessões de entrevista",
  },
  "es-419": {
    errInsufficientCredits: "Ya utilizó todas las sesiones de entrevista de su acceso de 30 días. Sus informes siguen disponibles.",
    noCreditsTitle: "Límite de sesiones de entrevista alcanzado",
    noCreditsBody: "Ya utilizó todas las sesiones de entrevista de su acceso de 30 días. Sus informes siguen disponibles.",
    creditsBalance: "Sesiones de entrevista",
  },
  "fr-CA": {
    errInsufficientCredits: "Vous avez utilisé toutes les sessions d’entrevue de votre accès de 30 jours. Vos rapports restent disponibles.",
    noCreditsTitle: "Limite de sessions d’entrevue atteinte",
    noCreditsBody: "Vous avez utilisé toutes les sessions d’entrevue de votre accès de 30 jours. Vos rapports restent disponibles.",
    creditsBalance: "Sessions d’entrevue",
  },
};

export const CURRENT_QUESTION_COPY = {
  en: "Current Question",
  "pt-BR": "Pergunta atual",
  "es-419": "Pregunta actual",
  "fr-CA": "Question actuelle",
};

export const SESSIONS_COPY = {
  en: { label: "Sessions used", until: "30-day access ends" },
  "pt-BR": { label: "Sessões usadas", until: "O acesso de 30 dias termina em" },
  "es-419": { label: "Sesiones usadas", until: "El acceso de 30 días termina el" },
  "fr-CA": { label: "Sessions utilisées", until: "L’accès de 30 jours se termine le" },
};

for (const [code, strings] of Object.entries(SESSION_LIMIT_COPY)) {
  if (UPLOAD_STRINGS[code]) Object.assign(UPLOAD_STRINGS[code], strings);
}

function ensureDomHost(parent, testId, className, before = null) {
  if (!parent) return null;
  let host = parent.querySelector(`:scope > [data-testid="${testId}"]`);
  if (!host) {
    host = document.createElement("div");
    host.setAttribute("data-testid", testId);
    if (before) parent.insertBefore(host, before);
    else parent.appendChild(host);
  }
  host.className = className;
  return host;
}

function ensurePersistentGroup(parent) {
  return ensureDomHost(
    parent,
    "standard-persistent-controls",
    "ml-7 lg:ml-9 flex shrink-0 items-center gap-3"
  );
}

export default function SessionAllowanceTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [allowanceTarget, setAllowanceTarget] = useState(null);
  const [accountTarget, setAccountTarget] = useState(null);

  const isInterview = useMemo(
    () => /^\/interview\/[^/]+$/.test(location.pathname),
    [location.pathname]
  );
  const isPrepare = /^\/prepare\/[^/]+$/.test(location.pathname);
  const isUpload = location.pathname === "/upload";
  const isReport = /^\/report\/[^/]+$/.test(location.pathname);
  const visible = isUpload || isPrepare || isInterview || isReport;

  const lang = normalizeLang(user?.preferred_language);
  const currentQuestionLabel = CURRENT_QUESTION_COPY[lang] || CURRENT_QUESTION_COPY.en;
  const sessionsCopy = SESSIONS_COPY[lang] || SESSIONS_COPY.en;

  // Standardize the header shell without forcing each screen to independently
  // reimplement global navigation. The immutable right-side cluster is always:
  // Sessions used -> Help -> Account. Contextual controls remain to its left.
  useEffect(() => {
    setAllowanceTarget(null);
    setAccountTarget(null);
    if (!visible || typeof document === "undefined") return undefined;

    let observer = null;
    const ownedNodes = new Set();

    const remember = (node) => {
      if (node) ownedNodes.add(node);
      return node;
    };

    const setTargets = (allowanceHost, accountHost = null) => {
      setAllowanceTarget((current) => (current === allowanceHost ? current : allowanceHost));
      setAccountTarget((current) => (current === accountHost ? current : accountHost));
    };

    const styleBrand = (header) => {
      if (!header) return;
      const logo = header.querySelector("img");
      if (logo) {
        logo.setAttribute("src", "/brand/globe-logo.svg");
        logo.setAttribute("alt", "English Job Interview Simulator globe logo");
        logo.className = "h-[56px] w-[76px] sm:h-[63px] sm:w-[86px] shrink-0 object-contain";
      }

      const legacyTitle = header.querySelector("span.font-semibold");
      if (legacyTitle) {
        legacyTitle.textContent = "ENGLISH JOB INTERVIEW SIMULATOR";
        legacyTitle.className = "hidden sm:block whitespace-nowrap text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand)] leading-tight";
      }
    };

    const attachUpload = () => {
      const shell = document.querySelector(".interview-setup-shell");
      const header = shell?.querySelector("header");
      const row = header?.firstElementChild;
      const rightControls = row?.lastElementChild;
      if (!header || !row || !rightControls) return false;

      row.className = "max-w-[1440px] mx-auto w-full px-3 sm:px-6 md:px-10 py-3.5 flex min-w-0 items-center gap-2 sm:gap-4";
      const brand = row.firstElementChild;
      if (brand) brand.className = "flex min-w-0 shrink-0 items-center gap-2 sm:gap-3 mr-auto";
      rightControls.className = "flex min-w-0 shrink-0 items-center gap-2 sm:gap-3";

      const first = rightControls.firstElementChild;
      const allowanceHost = ensureDomHost(
        rightControls,
        "standard-session-allowance-host",
        "shrink-0",
        first
      );
      if (allowanceHost && allowanceHost !== first) remember(allowanceHost);
      setTargets(allowanceHost, null);
      return true;
    };

    const attachReport = () => {
      const root = document.querySelector('[data-testid="report-container"]');
      const header = root?.querySelector("header");
      const row = header?.firstElementChild;
      const rightControls = row?.lastElementChild;
      if (!root || !header || !row || !rightControls) return false;

      styleBrand(header);
      row.className = "max-w-[1440px] mx-auto px-6 md:px-10 py-3.5 flex items-center gap-4";
      const brand = row.firstElementChild;
      if (brand) brand.className = "flex min-w-0 shrink-0 items-center gap-3 mr-auto";
      rightControls.className = "flex min-w-0 items-center gap-3";

      let group = rightControls.querySelector(':scope > [data-testid="standard-persistent-controls"]');
      if (!group) group = remember(ensurePersistentGroup(rightControls));
      const allowanceHost = ensureDomHost(group, "standard-session-allowance-host", "shrink-0");
      const accountHost = ensureDomHost(group, "standard-account-host", "shrink-0");
      setTargets(allowanceHost, accountHost);
      return true;
    };

    const attachWarmup = () => {
      const gate = document.querySelector('[data-testid="warmup-gate"]');
      const header = gate?.querySelector("header");
      const lifecycleHost = gate?.querySelector('[data-testid="warmup-lifecycle-host"]');
      if (!gate || !header || !lifecycleHost) return false;

      styleBrand(header);
      const row = header.firstElementChild;
      if (row) {
        row.className = "max-w-[1440px] mx-auto px-6 md:px-10 py-3.5 flex items-center gap-4";
        const brand = row.firstElementChild;
        if (brand && brand.tagName !== "IMG") {
          brand.className = "flex min-w-0 shrink-0 items-center gap-3 mr-auto";
        }
      }
      lifecycleHost.className = "ml-auto flex min-w-0 items-center gap-3";

      let group = lifecycleHost.querySelector(':scope > [data-testid="standard-persistent-controls"]');
      if (!group) group = remember(ensurePersistentGroup(lifecycleHost));
      const allowanceHost = ensureDomHost(group, "standard-session-allowance-host", "shrink-0");
      const accountHost = ensureDomHost(group, "standard-account-host", "shrink-0");
      setTargets(allowanceHost, accountHost);
      return true;
    };

    const attachActiveInterview = () => {
      const root = document.querySelector('[data-testid="interview-container"]');
      const header = root?.querySelector("header");
      const finishButton = header?.querySelector('[data-testid="finish-interview-button"]');
      const row = finishButton?.parentElement;
      if (!root || !header || !finishButton || !row) return false;

      styleBrand(header);
      row.className = "relative max-w-[1440px] mx-auto px-6 md:px-10 py-3.5 flex items-center gap-3";
      const brand = row.firstElementChild;
      if (brand) brand.className = "flex min-w-0 shrink-0 items-center gap-3 mr-auto";

      const seniority = header.querySelector('[data-testid="interview-seniority-badge"]');
      if (seniority) seniority.className = "hidden lg:block shrink-0";

      const progress = header.querySelector('[data-testid="progress-label"]');
      if (progress) {
        progress.className = "flex flex-col items-center gap-1 shrink-0";
        let progressLabel = progress.querySelector('[data-testid="current-question-label"]');
        if (!progressLabel) {
          progressLabel = document.createElement("div");
          progressLabel.setAttribute("data-testid", "current-question-label");
          progress.insertBefore(progressLabel, progress.firstChild);
        }
        // MutationObserver watches childList changes. Writing the same
        // textContent on every sync creates a self-triggering mutation loop
        // when the warm-up transitions into the real interview. Only write
        // when the visible label actually changes.
        if (progressLabel.textContent !== currentQuestionLabel) {
          progressLabel.textContent = currentQuestionLabel;
        }
        progressLabel.className = "whitespace-nowrap text-[8px] uppercase tracking-[0.10em] leading-none text-[color:var(--text-secondary)]";
      }

      let group = row.querySelector(':scope > [data-testid="standard-persistent-controls"]');
      if (!group) group = remember(ensurePersistentGroup(row));
      const allowanceHost = ensureDomHost(group, "standard-session-allowance-host", "shrink-0");
      const accountHost = ensureDomHost(group, "standard-account-host", "shrink-0");
      setTargets(allowanceHost, accountHost);
      return true;
    };

    // Advanced Mode (2026-11-02): sessions used and the account menu in its own header.
    const attachLive = () => {
      const host = document.querySelector('[data-testid="live-interview"] [data-testid="live-account-host"]');
      if (!host) return false;
      let group = host.querySelector(':scope > [data-testid="standard-persistent-controls"]');
      if (!group) group = remember(ensureDomHost(host, "standard-persistent-controls", "flex shrink-0 items-center gap-3"));
      const allowanceHost = ensureDomHost(group, "standard-session-allowance-host", "shrink-0");
      const accountHost = ensureDomHost(group, "standard-account-host", "shrink-0");
      setTargets(allowanceHost, accountHost);
      return true;
    };

    const attachPrepare = () => {
      const header = document.querySelector('[data-testid="app-header"]');
      const allowanceHost = header?.querySelector('[data-testid="standard-session-allowance-host"]');
      if (!header || !allowanceHost) return false;
      setTargets(allowanceHost, null);
      return true;
    };

    const sync = () => {
      if (isPrepare) return attachPrepare();
      if (isUpload) return attachUpload();
      if (isReport) return attachReport();
      if (isInterview) {
        if (document.querySelector('[data-testid="live-interview"]')) return attachLive();
        if (document.querySelector('[data-testid="warmup-gate"]')) return attachWarmup();
        return attachActiveInterview();
      }
      return false;
    };

    sync();
    observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (observer) observer.disconnect();
      ownedNodes.forEach((node) => {
        if (node?.parentElement) node.remove();
      });
      setAllowanceTarget(null);
      setAccountTarget(null);
    };
  }, [visible, isUpload, isPrepare, isInterview, isReport, location.pathname, currentQuestionLabel]);

  useEffect(() => {
    if (!visible || !user || user?.is_admin) {
      setUsage(null);
      return undefined;
    }

    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/session-allowance");
        if (alive) setUsage(data || null);
      } catch {
        if (alive) setUsage(null);
      }
    };

    load();
    const timer = window.setInterval(load, 30000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [visible, user?.user_id, user?.is_admin, location.pathname]);

  const sharedStyles = (
    <style>{`
      .interview-setup-shell > header,
      [data-testid="warmup-gate"] > header,
      [data-testid="interview-container"] > header,
      [data-testid="report-container"] > header,
      [data-testid="app-header"] {
        background-color: rgba(255,255,255,0.98) !important;
      }

      /* InterviewLifecycleControls owns the warm-up lifecycle host but its
         legacy max-width must not pull this one screen inward. */
      [data-testid="warmup-gate"] > header > div {
        max-width: 1440px !important;
        padding-left: 2.5rem !important;
        padding-right: 2.5rem !important;
        padding-top: 0.875rem !important;
        padding-bottom: 0.875rem !important;
      }

      [data-testid="standard-persistent-controls"] [data-testid="help-button"],
      [data-testid="standard-persistent-controls"] [data-testid="user-menu-button"],
      .interview-setup-shell header [data-testid="help-button"],
      .interview-setup-shell header [data-testid="user-menu-button"] {
        height: 2.5rem !important;
        padding-left: 1rem !important;
        padding-right: 1rem !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        font-size: 0.875rem !important;
        line-height: 1 !important;
        white-space: nowrap !important;
      }

      [data-testid="session-allowance-tracker"] {
        height: 2.5rem !important;
        display: inline-flex !important;
        align-items: center !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        white-space: nowrap !important;
      }

      [data-testid="interview-container"] [data-testid="restart-interview-btn"] + span {
        display: none !important;
      }
      [data-testid="interview-container"] [data-testid="next-question-button"],
      [data-testid="interview-container"] [data-testid="interview-mobile-next"] {
        display: none !important;
      }
      [data-testid="interview-container"] [data-testid="progress-label"] > div:last-child {
        display: none !important;
      }
      [data-testid="interview-container"] [data-testid="finish-interview-button"] {
        flex-shrink: 0 !important;
        white-space: nowrap !important;
      }
      [data-testid="interview-container"] [data-testid="restart-interview-lifecycle-btn"],
      [data-testid="interview-container"] [data-testid="end-interview-btn"],
      [data-testid="interview-container"] [data-testid="play-question-button"] {
        flex-shrink: 0 !important;
        white-space: nowrap !important;
      }

      /* Question toolbar: category/focus text gives way first. The action group
         remains a single non-wrapping unit on desktop so the final button can
         never fall onto an accidental one-button second row. */
      [data-testid="interview-container"] main section > div.mb-4 {
        flex-wrap: nowrap !important;
      }
      [data-testid="interview-container"] main section > div.mb-4 > div:first-child {
        min-width: 0 !important;
        flex: 1 1 auto !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }
      [data-testid="interview-container"] main section > div.mb-4 > div:last-child {
        flex: 0 0 auto !important;
        flex-wrap: nowrap !important;
      }

      [data-testid="warmup-gate"] [data-testid="warmup-lifecycle-host"] > [data-testid="end-interview-btn"] {
        height: 2.5rem !important;
        flex-shrink: 0 !important;
        white-space: nowrap !important;
      }

      @media (max-width: 900px) {
        [data-testid="warmup-gate"] > header > div {
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
        }
        [data-testid="interview-container"] main section > div.mb-4 {
          align-items: stretch !important;
          flex-direction: column !important;
        }
        [data-testid="interview-container"] main section > div.mb-4 > div:last-child {
          width: 100% !important;
          overflow-x: auto !important;
          justify-content: flex-start !important;
          padding-bottom: 0.25rem !important;
        }
        [data-testid="standard-persistent-controls"] {
          margin-left: 1rem !important;
        }
      }

      @media (max-width: 640px) {
        .interview-setup-shell > header > div,
        [data-testid="warmup-gate"] > header > div,
        [data-testid="interview-container"] > header > div,
        [data-testid="report-container"] > header > div,
        [data-testid="app-header"] > div {
          width: 100% !important;
          max-width: 100% !important;
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }

        /* The warm-up has a dedicated sticky mobile CTA. Keep only that one
           below the sm breakpoint so users never see two Continue buttons. */
        [data-testid="warmup-gate"] [data-testid="warmup-continue"] {
          display: none !important;
        }

        /* Warm-up still originates from legacy header markup. At phone width,
           hide the long descriptor and compact the contextual End Interview
           control so the persistent Help/Account controls fit without
           horizontal scrolling. */
        [data-testid="warmup-gate"] > header > div > div:not([data-testid="warmup-lifecycle-host"]) {
          display: none !important;
        }
        [data-testid="warmup-gate"] > header > div > img {
          height: 3.5rem !important;
          width: 4.75rem !important;
          flex: 0 0 auto !important;
          margin-right: auto !important;
          object-fit: contain !important;
        }
        [data-testid="warmup-gate"] [data-testid="warmup-lifecycle-host"] {
          margin-left: auto !important;
          gap: 0.5rem !important;
          flex-shrink: 0 !important;
        }
        [data-testid="warmup-gate"] [data-testid="warmup-lifecycle-host"] > [data-testid="end-interview-btn"] {
          width: 2.5rem !important;
          min-width: 2.5rem !important;
          padding: 0 !important;
          gap: 0 !important;
          justify-content: center !important;
          font-size: 0 !important;
          overflow: hidden !important;
        }

        [data-testid="standard-persistent-controls"] {
          margin-left: 0.5rem !important;
          gap: 0.5rem !important;
        }

        [data-testid="warmup-gate"] [data-testid="standard-persistent-controls"] {
          margin-left: 0 !important;
        }

        [data-testid="standard-persistent-controls"] [data-testid="help-button"],
        [data-testid="standard-persistent-controls"] [data-testid="user-menu-button"],
        .interview-setup-shell header [data-testid="help-button"],
        .interview-setup-shell header [data-testid="user-menu-button"] {
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }

        [data-testid="session-allowance-tracker"] {
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }
      }
    `}</style>
  );

  const allowance = usage && !user?.is_admin && !usage?.limit_disabled ? (
    <div
      data-testid="session-allowance-tracker"
      role="status"
      aria-live="polite"
      aria-label={`${sessionsCopy.label}: ${usage.used} of ${usage.limit}`}
      title={usage.period_end ? `${sessionsCopy.until} ${new Date(usage.period_end).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" })}` : undefined}
      className="shrink-0 rounded-full border border-[color:var(--border-stone)] bg-white px-4 text-sm font-medium text-[color:var(--text-secondary)] shadow-sm"
    >
      <span className="hidden sm:inline">{sessionsCopy.label}: </span>
      <span>{usage.used} of {usage.limit}</span>
    </div>
  ) : null;

  return (
    <>
      {isPrepare && <AppHeader />}
      {sharedStyles}
      {allowance && allowanceTarget ? createPortal(allowance, allowanceTarget) : null}
      {accountTarget
        ? createPortal(
            <UserMenu
              showHelp={false}
              buttonClassName="h-10 px-4 py-0 text-sm"
            />,
            accountTarget
          )
        : null}
    </>
  );
}
